import config from '@/config';
import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import commonUtils from './common';
import store from '@/store';
import { refreshTokens } from '@/services/tokenExchange';
import { errorActions } from '@/store/reducers/error';
import { uiActions } from '@/store/reducers/ui';
import { isForcedSessionAuthError, triggerForcedSessionLogout } from '@/utils/forcedSessionLogout';
import { isWafBlockedError } from '@/utils/wafBlockedError';
import i18nInstance from '@/locales/i18n';
import wafBlockedIcon from '@/assets/svg/waf-blocked.svg';

const axiosInstance = axios.create({
  baseURL: config.apiUrl,
  withCredentials: false,
});

axiosInstance.interceptors.request.use(async config => {
  try {
    if (
      !!localStorage.getItem('token') &&
      commonUtils.shouldTokenRefresh(localStorage.getItem('token') ?? '')
    ) {
      const newToken = await refreshTokens(store.dispatch);
      if (newToken) {
        localStorage.setItem('token', newToken);
      }
    }

    const token = localStorage.getItem('token');

    if (token && commonUtils.isTokenExpired(token)) {
      return Promise.reject(
        new axios.Cancel('Aborted request: access token is expired and could not be refreshed.')
      );
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    const googleToken = localStorage.getItem('X-google-token');
    if (googleToken && googleToken !== 'undefined' && googleToken !== 'null') {
      config.headers['X-google-token'] = googleToken;
    }

    return config;
  } catch (error) {
    console.error('Error in request interceptor:', error);
    return Promise.reject(error);
  }
});

axiosInstance.interceptors.response.use(
  response => {
    // Check if the response status code is 204
    if (response.status === 204) {
      throw new Error('No Content');
    }

    return response;
  },
  async (error: AxiosError) => {
    if (isWafBlockedError(error)) {
      const isAuthenticated = !!localStorage.getItem('token');

      if (!isAuthenticated) {
        window.location.assign(new URL('/waf-blocked', window.location.origin).toString());
        return Promise.reject(error);
      }

      const popupPayload = {
        eyebrow: i18nInstance.t('error.wafBlocked.eyebrow'),
        title: i18nInstance.t('error.wafBlocked.title'),
        message: i18nInstance.t('error.wafBlocked.description'),
        buttonText: i18nInstance.t('homepage'),
        icon: wafBlockedIcon,
        isWaf: true,
      };
      store.dispatch(errorActions.showError(popupPayload));
      store.dispatch(uiActions.showErrorModal(popupPayload));
      return Promise.reject(error);
    }

    const status = error.response?.status;
    const message = (error.response as { data?: { message?: string } } | undefined)?.data?.message;
    if (
      status === 403 &&
      message === 'User registration limit reached. No new users can be registered at this time.'
    ) {
      return Promise.reject(error);
    }

    const originalRequest = error.config as
      | (InternalAxiosRequestConfig & { _retry?: boolean })
      | undefined;
    const hadBearer = !!localStorage.getItem('token');
    const localToken = localStorage.getItem('token') ?? '';
    const shouldAttemptReauth =
      status === 401 ||
      (status === 403 &&
        (commonUtils.isTokenExpired(localToken) || isForcedSessionAuthError(error)));

    if (originalRequest && !originalRequest._retry && hadBearer && shouldAttemptReauth) {
      originalRequest._retry = true;

      const newToken = await refreshTokens(store.dispatch, { showPopupOnFailure: true });
      if (newToken) {
        localStorage.setItem('token', newToken);
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return axiosInstance(originalRequest);
      }

      return Promise.reject(error);
    }

    // Safety net for recognized session-invalid responses on other status codes.
    if (isForcedSessionAuthError(error)) {
      triggerForcedSessionLogout();
      return Promise.reject(error);
    }

    if (error.response && error.response.status !== 200) {
      store.dispatch(errorActions.showError({}));
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
